import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChefHat, QrCode, Smartphone, BarChart3, Globe2, Bell, Bike,
  CreditCard, ArrowRight, Check, Star, Zap, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useLandingT } from '../i18n/landingTranslations';

export default function LandingPage() {
  const { language } = useLanguage();
  const t = useLandingT(language);

  const FEATURES = [
    { icon: QrCode, title: t('feat_qr_title'), desc: t('feat_qr_desc'), color: '#D4A853' },
    { icon: Smartphone, title: t('feat_pos_title'), desc: t('feat_pos_desc'), color: '#E16833' },
    { icon: ChefHat, title: t('feat_kitchen_title'), desc: t('feat_kitchen_desc'), color: '#2D6A4F' },
    { icon: Bike, title: t('feat_grab_title'), desc: t('feat_grab_desc'), color: '#00B14F' },
    { icon: BarChart3, title: t('feat_accounting_title'), desc: t('feat_accounting_desc'), color: '#1B3A4B' },
    { icon: Globe2, title: t('feat_lang_title'), desc: t('feat_lang_desc'), color: '#7C3AED' },
    { icon: Bell, title: t('feat_notif_title'), desc: t('feat_notif_desc'), color: '#EF4444' },
    { icon: CreditCard, title: t('feat_pay_title'), desc: t('feat_pay_desc'), color: '#0EA5E9' },
  ];

  // Currency by language for pricing display
  const LANG_CURRENCY = {
    fr: { code: 'EUR', symbol: '\u20ac', rates: [19, 39, 79] },
    en: { code: 'USD', symbol: '$', rates: [19, 39, 79] },
    th: { code: 'THB', symbol: '\u0e3f', rates: [690, 1390, 2790] },
    de: { code: 'EUR', symbol: '\u20ac', rates: [19, 39, 79] },
    es: { code: 'EUR', symbol: '\u20ac', rates: [19, 39, 79] },
    ru: { code: 'USD', symbol: '$', rates: [19, 39, 79] },
    ar: { code: 'AED', symbol: 'AED', rates: [69, 139, 289] },
    zh: { code: 'USD', symbol: '$', rates: [19, 39, 79] },
  };
  const curr = LANG_CURRENCY[language] || LANG_CURRENCY.en;

  const PLANS = [
    {
      name: t('plan_starter_name'), price: curr.rates[0].toLocaleString(), description: t('plan_starter_desc'),
      features: ['pf_tables_5','pf_servers_2','pf_qr_menu','pf_realtime','pf_cash_card','pf_kitchen_bar','pf_email_support'],
      cta: t('plan_starter_cta'), popular: false, color: '#6E5E52',
    },
    {
      name: t('plan_pro_name'), price: curr.rates[1].toLocaleString(), description: t('plan_pro_desc'),
      features: ['pf_tables_20','pf_servers_10','pf_all_starter','pf_grab','pf_accounting','pf_promo','pf_loyalty','pf_push','pf_priority_support'],
      cta: t('plan_pro_cta'), popular: true, color: '#D4A853',
    },
    {
      name: t('plan_enterprise_name'), price: curr.rates[2].toLocaleString(), description: t('plan_enterprise_desc'),
      features: ['pf_unlimited_tables','pf_unlimited_servers','pf_all_pro','pf_multi_restaurant','pf_branding','pf_api','pf_printer','pf_onboarding','pf_247_support'],
      cta: t('plan_enterprise_cta'), popular: false, color: '#0A422D',
    },
  ];

  const STEPS = [
    { step: '01', title: t('step_1_title'), desc: t('step_1_desc') },
    { step: '02', title: t('step_2_title'), desc: t('step_2_desc') },
    { step: '03', title: t('step_3_title'), desc: t('step_3_desc') },
  ];

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white overflow-hidden">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-[#0C0C0C]/80 backdrop-blur-xl border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ChefHat className="w-7 h-7 text-[#D4A853]" />
            <span className="text-xl font-black tracking-tight">HIBI<span className="text-[#D4A853]">SAAS</span></span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-[#999]">
            <a href="#features" className="hover:text-white transition-colors">{t('nav_features')}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t('nav_pricing')}</a>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSelector variant="dark" />
            <Link to="/auth" className="block">
              <Button variant="ghost" className="text-[#999] hover:text-white text-sm h-9 px-4" data-testid="nav-login">
                {t('nav_login')}
              </Button>
            </Link>
            <Link to="/auth">
              <Button className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-5 rounded-full whitespace-nowrap" data-testid="nav-signup">
                {t('nav_signup')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4A853]/10 border border-[#D4A853]/20 mb-8">
            <Zap className="w-3.5 h-3.5 text-[#D4A853]" />
            <span className="text-xs font-medium text-[#D4A853]">{t('hero_badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-6">
            {t('hero_title_1')}<br />
            <span className="text-[#D4A853]">{t('hero_title_2')}</span><br />
            {t('hero_title_3')}
          </h1>
          <p className="text-base sm:text-lg text-[#888] max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('hero_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <Button className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold h-13 px-8 rounded-full text-base" data-testid="hero-cta">
                {t('hero_cta')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="#features">
              <Button variant="outline" className="border-[#333] text-white hover:bg-[#1A1A1A] h-13 px-8 rounded-full text-base bg-transparent">
                {t('hero_discover')}
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-16 max-w-lg mx-auto">
            <div>
              <p className="text-2xl sm:text-3xl font-black text-[#D4A853]">8</p>
              <p className="text-xs text-[#666]">{t('stat_languages')}</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-black text-[#D4A853]">{"<2min"}</p>
              <p className="text-xs text-[#666]">{t('stat_install')}</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-black text-[#D4A853]">0%</p>
              <p className="text-xs text-[#666]">{t('stat_commission')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-black mb-3">{t('features_title')}</h2>
            <p className="text-[#888] text-sm sm:text-base max-w-xl mx-auto">{t('features_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="group bg-[#111] border border-[#1E1E1E] rounded-2xl p-5 hover:border-[#333] transition-all hover:-translate-y-1 duration-300">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.color}15` }}>
                  <f.icon className="w-5 h-5" style={{ color: f.color }} />
                </div>
                <h3 className="font-bold text-white text-sm mb-1.5">{f.title}</h3>
                <p className="text-xs text-[#888] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 bg-[#080808]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-14">{t('steps_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#D4A853]/10 border border-[#D4A853]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-black text-[#D4A853]">{s.step}</span>
                </div>
                <h3 className="font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-[#888]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-black mb-3">{t('pricing_title')}</h2>
            <p className="text-[#888] text-sm sm:text-base">{t('pricing_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((plan, i) => (
              <div key={i} className={`rounded-3xl p-6 border transition-all ${
                plan.popular
                  ? 'bg-[#1A1600] border-[#D4A853]/40 relative'
                  : 'bg-[#111] border-[#1E1E1E] hover:border-[#333]'
              }`} data-testid={`plan-${plan.name.toLowerCase()}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#D4A853] text-black text-xs font-bold">
                    {t('popular_badge')}
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="font-bold text-lg" style={{ color: plan.color }}>{plan.name}</h3>
                  <p className="text-xs text-[#888] mt-0.5">{plan.description}</p>
                </div>
                <div className="mb-5">
                  <span className="text-3xl font-black text-white">{plan.price}</span>
                  <span className="text-sm text-[#888]"> {curr.symbol}{t('plan_per_month')}</span>
                </div>
                <Link to="/auth">
                  <Button className={`w-full h-11 rounded-xl font-bold text-sm ${
                    plan.popular
                      ? 'bg-[#D4A853] hover:bg-[#C49A48] text-black'
                      : 'bg-[#222] hover:bg-[#333] text-white'
                  }`}>
                    {plan.cta}
                  </Button>
                </Link>
                <ul className="mt-5 space-y-2.5">
                  {plan.features.map((fKey, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-[#2D6A4F] flex-shrink-0 mt-0.5" />
                      <span className="text-[#CCC]">{t(fKey)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-6 bg-[#080808]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-[#D4A853] fill-[#D4A853]" />)}
          </div>
          <blockquote className="text-lg sm:text-xl text-white font-medium mb-4 leading-relaxed">
            {t('testimonial_text')}
          </blockquote>
          <p className="text-sm text-[#888]">{t('testimonial_author')}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-4">{t('cta_title')}</h2>
          <p className="text-[#888] mb-8">{t('cta_subtitle')}</p>
          <Link to="/auth">
            <Button className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold h-13 px-10 rounded-full text-base" data-testid="final-cta">
              {t('cta_btn')} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1A1A1A] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-[#D4A853]" />
              <span className="font-bold text-sm">HIBI<span className="text-[#D4A853]">SAAS</span></span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-[#666]">
              <Link to="/legal/terms" className="hover:text-white transition-colors">{t('footer_terms')}</Link>
              <Link to="/legal/terms_of_use" className="hover:text-white transition-colors">{t('footer_terms_of_use')}</Link>
              <Link to="/legal/privacy" className="hover:text-white transition-colors">{t('footer_privacy')}</Link>
              <Link to="/legal/legal_notice" className="hover:text-white transition-colors">{t('footer_legal')}</Link>
              <Link to="/legal/contact" className="hover:text-white transition-colors">{t('footer_contact')}</Link>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#666]">
              <Shield className="w-3.5 h-3.5" />
              <span>{t('footer_secure')}</span>
            </div>
          </div>
          <div className="text-center mt-4 text-[10px] text-[#444]">
            {t('footer_company')}
          </div>
        </div>
      </footer>
    </div>
  );
}
